<template>
    <div>
    <base-dialog :loading="loading" :value="dialog" :width="480"
        :title="`${editMode ? $t('common.edit') : $t('common.add')} ${$t('common.note')}`"
        @input="val => { if (!val) hide() }" @close="hide">
        <div class="note-body">
            <div class="note-row">
                <kimsa-text-field bolder-label dense readonly :label="$t('common.date')" :value="date" force-value />
                <kimsa-combobox dense solo no-object multiple-with-chips :label="$t('common.remind_me')"
                    :placeholder="$t('common.select')" :items="reminderOptions" :value="reminders"
                    @change="reminders = $event || []" />
            </div>
            <kimsa-toggle class="show-row" label-left :value="showPopup" @change="showPopup = $event">
                <span class="label bolder">{{ $t('common.show_to_student') }}</span>
            </kimsa-toggle>
            <kimsa-textarea-field bolder-label :label="$t('common.message')" :rows="5"
                :placeholder="$t('common.write_here')" @change="message = $event" :value="message" force-value />
        </div>

        <template v-slot:footer>
            <v-btn v-if="editMode" block depressed x-large color="error" class="footer-btn"
                :loading="loadingDelete" @click="deleteNotes">
                {{ $t('common.delete') }}
            </v-btn>
            <v-btn block depressed x-large color="primary" class="footer-btn black--text"
                :disabled="disabled" @click="setNotes" :loading="loading">
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </base-dialog>

    <kimsa-confirm ref="confirmDelete" :title="$t('common.delete_note')" :content="$t('common.confirm_delete_note')"
        :loading="loadingDelete" :persistent="false" :close-on-confirm="false" :alert-mode="true"
        @confirm="onConfirmDelete" />
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaTextareaField from "@/components/common/inputs/KimsaTextareaField";
import KimsaToggle from "@/components/common/inputs/KimsaToggle";
import BaseDialog from "@/components/common/BaseDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";

const REMINDER_DAYS = [1, 3, 7, 15, 30]
const DEFAULT_REMINDERS = [1]

export default {
    name: "NotesRoutineDialog",
    components: { KimsaTextareaField, KimsaTextField, KimsaCombobox, KimsaToggle, BaseDialog, KimsaConfirm },
    props: {
        currentSelection: {
            type: Object,
            default: null,
        },
        routineNumber: {
            type: Number,
            default: 1
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingDelete: false,
            routineNote: null,
            date: null,
            showPopup: false,
            message: '',
            editMode: false,
            routineNoteDate: null,
            reminders: [],
        }
    },
    computed: {
        disabled(vm = this) {
            return !vm.message
        },
        routineNoteId(vm = this) {
            return vm.routineNote?.id || null
        },
        coachId(vm = this) {
            return vm.currentUser?.uid || null
        },
        reminderOptions(vm = this) {
            return REMINDER_DAYS.map(days => ({ name: vm.$tc('common.days_before', days, { n: days }), value: days }))
        }
    },
    methods: {
        show(routineNote, date) {
            const vm = this
            vm.setDefaults(routineNote, date)
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.routineNote = null
            vm.message = ''
            vm.date = null
            vm.showPopup = false
            vm.editMode = false
            vm.routineNoteDate = null
            vm.reminders = []
        },
        setDefaults(routineNote, date) {
            const vm = this
            vm.routineNote = routineNote
            vm.message = routineNote?.message || ''
            vm.date = routineNote?.messageDate || vm.$moment().format('DD-MM-YYYY')
            vm.showPopup = routineNote?.showPopup || false
            vm.editMode = !!vm.message
            vm.routineNoteDate = date
            vm.reminders = Array.isArray(routineNote?.reminders) ? [...routineNote.reminders] : [...DEFAULT_REMINDERS]
        },
        getGroupId() {
            const vm = this
            if (vm.currentSelection?.id || vm.currentSelection?.id === 0) return vm.currentSelection.id
            return null
        },
        async setNotes() {
            const vm = this
            if (vm.loadingDelete) return;
            await vm.withLoading(async () => {
                let noteDate = vm.$moment(vm.routineNoteDate)
                let dateFormatted = noteDate.format('YYYY-MM-DD')
                const [year, month, day] = dateFormatted.split('-');

                let payload = {
                    studentId: vm.currentSelection?.objectID || null,
                    teamId: vm.currentSelection?.teamId || null,
                    groupId: vm.getGroupId(),
                    date: Timestamp.fromDate(noteDate.toDate()),
                    rawDate: {
                        full: dateFormatted,
                        day: day,
                        month: month,
                        year: year
                    },
                    message: vm.message,
                    messageDate: vm.date,
                    showPopup: vm.showPopup,
                    reminders: vm.reminders,
                    coachId: vm.coachId,
                    routineNumber: vm.routineNote?.routineNumber || vm.routineNumber
                }

                vm.routineNoteId ?
                    await db.collection('routineNotes').doc(vm.routineNoteId).update(payload) :
                    await db.collection('routineNotes').add(payload)

                vm.toast(vm.$t('common.note_saved'))
                vm.$emit('saved', dateFormatted)
                vm.hide()
            })
        },
        deleteNotes() {
            const vm = this
            if (vm.loading) return;
            vm.$refs.confirmDelete.show()
        },
        async onConfirmDelete() {
            const vm = this
            await vm.withLoading(async () => {
                await db.collection('routineNotes').doc(vm.routineNoteId).delete()

                let dateFormatted = vm.$moment(vm.routineNoteDate).format('YYYY-MM-DD')

                vm.$refs.confirmDelete?.hide()
                vm.toast(vm.$t('common.note_deleted'))
                vm.$emit('saved', dateFormatted)
                vm.hide()
            }, 'loadingDelete')
        }
    },
}
</script>

<style scoped>
.note-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.note-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.show-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.label {
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
}

.footer-btn {
    border-radius: 0 !important;
    height: 52px !important;
    font-weight: 700;
}
</style>